/**
 * Turning a BROKER's verbatim rejection text into a safe, actionable link.
 *
 * WHY THIS EXISTS
 * ---------------
 * When CQG refuses a logon because the trader has never accepted CQG's End User License Agreement,
 * the refusal arrives as prose with a URL in it, e.g.
 *
 *   "CQG logon rejected (code 101): You must accept the terms of the End User License Agreement.
 *    Visit: https://my.cqg.com/token/WVTT-biCTvGkZtkkDl*kqA ."
 *
 * The BE relays CQG's `LogonResult.text_message` verbatim (`SessionHandlers.cs`), so the trader used
 * to get a dead plain-text URL and no way forward.
 *
 * WHY WE CANNOT KEY OFF THE RESULT CODE
 * -------------------------------------
 * `user_session_2.proto`'s `LogonResult.ResultCode` has exactly SUCCESS 0, FAILURE 101,
 * NO_ONETIME_PASSWORD 103, PASSWORD_EXPIRED 104, CONCURRENT_SESSION 105, REDIRECTED 106,
 * ROUTINE_ERROR 107, ACCESS_TOKEN_EXPIRED 108 (verified in the proto, 2026-09-15). There is NO
 * agreement-specific code: the EULA refusal is the GENERIC 101 — the same code a wrong password
 * uses. So the code tells us nothing, and CQG's English prose can be reworded at any time.
 *
 * THE DETECTION RULE
 * ------------------
 *  - PRIMARY (structural): the message contains an `https` URL whose host is on the broker
 *    allowlist below. A broker refusing a logon while pointing at its OWN domain is a strong
 *    "go here to fix it" signal that survives any rewording.
 *  - SECONDARY (prose): an agreement/licence/terms/EULA phrase picks the specific headline copy.
 *    Without it we still show the friendly frame + button, worded so it does not claim to know
 *    exactly what the broker wants.
 *  - NO allowlisted URL ⇒ `null`, and the caller renders the raw text completely unchanged.
 *
 * SECURITY
 * --------
 * This text crosses a trust boundary (CQG → our BE → the browser), so:
 *  - only `https:` is ever accepted — `http:`, `javascript:`, `data:` and friends never link;
 *  - the host is parsed with the `URL` API (which correctly resolves userinfo tricks such as
 *    `https://cqg.com@evil.example/x` to host `evil.example`) and compared against the allowlist
 *    with a LEADING DOT, so `evilcqg.com` and `cqg.com.attacker.net` both fail;
 *  - the caller builds React elements — never `dangerouslySetInnerHTML` — so HTML in the broker's
 *    text is impossible to inject by construction.
 */

/**
 * THE broker-link allowlist: registrable domains whose pages we are willing to make clickable out
 * of a broker's own verbatim error text. Matching is `host === domain || host.endsWith('.'+domain)`,
 * so subdomains (`my.cqg.com`) pass while look-alikes (`evilcqg.com`, `cqg.com.attacker.net`) do not.
 *
 * Adding another broker is ONE edit here — e.g. `['cqg.com', 'rithmic.com']`. Only add a domain the
 * broker itself controls end-to-end; anything not listed stays inert plain text.
 */
export const BROKER_LINK_HOSTS: readonly string[] = ['cqg.com'];

/** Which headline copy the caller should use for a detected broker action. */
export type BrokerActionKind = 'agreement' | 'unknown';

export interface BrokerAction {
  /** The safe, allowlisted `https` URL, with trailing sentence punctuation stripped. */
  url: string;
  /** `agreement` when the prose names an agreement/licence/terms; otherwise `unknown`. */
  kind: BrokerActionKind;
}

/** Candidate URL tokens. Deliberately `https` only — no other scheme can even be captured. */
const HTTPS_TOKEN = /https:\/\/[^\s<>"'`]+/gi;

/** Sentence punctuation a broker may glue onto the end of a URL. Never part of the href. */
const TRAILING_PUNCTUATION = /[.,;:!?'"”’»]+$/;

/** The secondary, prose-level signal that this is specifically an agreement to accept. */
const AGREEMENT_PHRASE = /\b(agreements?|licen[cs]e[ds]?|licensing|terms|eula)\b/i;

/**
 * Strip trailing sentence punctuation: `…/token/AB*cd .` and `…/token/AB*cd.` must both yield
 * `…/token/AB*cd`. A closing bracket is only punctuation when nothing inside the URL opened it,
 * so a genuine `…/wiki/Foo_(bar)` keeps its parenthesis.
 */
function trimTrailingPunctuation(candidate: string): string {
  let url = candidate.replace(TRAILING_PUNCTUATION, '');
  while (/[)\]}]$/.test(url)) {
    const close = url.slice(-1);
    const open = close === ')' ? '(' : close === ']' ? '[' : '{';
    if (url.includes(open)) break;
    url = url.slice(0, -1).replace(TRAILING_PUNCTUATION, '');
  }
  return url;
}

/**
 * `true` only for an absolute `https` URL whose host is on {@link BROKER_LINK_HOSTS}. Exported so
 * the host rule itself is directly testable (look-alike hosts, non-https schemes).
 */
export function isAllowlistedBrokerUrl(raw: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return false;                                   // not a URL at all
  }
  if (parsed.protocol !== 'https:') return false;   // https ONLY — never http:, javascript:, data:
  const host = parsed.hostname.toLowerCase();
  // The leading dot is load-bearing: without it `evilcqg.com` would end with `cqg.com` and pass.
  return BROKER_LINK_HOSTS.some(domain => host === domain || host.endsWith(`.${domain}`));
}

/**
 * Find the broker's own "go here to fix it" link inside a verbatim rejection message.
 * Returns `null` when there is none — the caller must then render the raw text unchanged.
 */
export function detectBrokerAction(message: string | null | undefined): BrokerAction | null {
  if (!message) return null;
  const kind: BrokerActionKind = AGREEMENT_PHRASE.test(message) ? 'agreement' : 'unknown';
  for (const match of message.match(HTTPS_TOKEN) ?? []) {
    const url = trimTrailingPunctuation(match);
    if (isAllowlistedBrokerUrl(url)) return { url, kind };
  }
  return null;
}
